import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import VehicleImagePlaceholder from './VehicleImagePlaceholder'
import './VehicleCard.css'

function formatCardPrice(vehicle) {
  if (vehicle.priceLabel) {
    const m = String(vehicle.priceLabel).match(/^([A-Z]{3})\s+(.+)$/i)
    if (m) return { currency: m[1].toUpperCase(), amount: m[2] }
  }
  const currency = vehicle.currency || 'USD'
  const amount = new Intl.NumberFormat('es-UY', { maximumFractionDigits: 0 }).format(vehicle.price ?? 0)
  return { currency, amount }
}

function getCardLines(vehicle) {
  const line1 =
    vehicle.titleLine1 ||
    [vehicle.brand, vehicle.model].filter(Boolean).join(' ').trim() ||
    vehicle.title ||
    'Vehículo'
  const line2 = vehicle.titleLine2 ?? ''
  const year = vehicle.year != null && vehicle.year !== '' ? String(vehicle.year) : '—'
  const km = vehicle.mileage != null ? Number(vehicle.mileage) : 0
  return { line1, line2, year, km }
}

function VehicleCardMedia({ vehicle, alt }) {
  if (vehicle.image) {
    return <img src={vehicle.image} alt={alt} className="vehicle-card__image" loading="lazy" />
  }
  return <VehicleImagePlaceholder className="vehicle-card__image vehicle-card__image--empty" />
}

function VehicleCard({ vehicle, variant = 'grid' }) {
  const { t } = useTranslation()
  const { line1, line2, year, km } = getCardLines(vehicle)
  const { currency, amount } = formatCardPrice(vehicle)

  if (variant === 'modern') {
    return (
      <Link to={`/vehiculos/${vehicle.id}`} className="vehicle-card-link vehicle-card-link--modern">
        <article className="vehicle-card vehicle-card--modern">
          <div className="vehicle-card__image-wrap">
            <VehicleCardMedia vehicle={vehicle} alt={line1} />
            <div className="vehicle-card__badges">
              <span className="vehicle-card__badge">{year}</span>
              <span className="vehicle-card__badge">{km.toLocaleString('es-UY')} km</span>
            </div>
            <span className="vehicle-card__arrow" aria-hidden>
              →
            </span>
          </div>
          <div className="vehicle-card__body">
            <h3 className="vehicle-card__line1">{line1}</h3>
            {line2 ? <p className="vehicle-card__line2">{line2}</p> : null}
            <div className="vehicle-card__footer">
              <p className="vehicle-card__price">
                <span className="vehicle-card__currency">{currency}</span> {amount}
              </p>
              <span className="vehicle-card__cta">
                {t('listings.card.viewDetail')}
                <span className="vehicle-card__cta-arrow" aria-hidden>
                  →
                </span>
              </span>
            </div>
          </div>
        </article>
      </Link>
    )
  }

  if (variant === 'list') {
    return (
      <Link to={`/vehiculos/${vehicle.id}`} className="vehicle-card-link vehicle-card-link--list">
        <article className="vehicle-card vehicle-card--list">
          <div className="vehicle-card__image-wrap">
            <VehicleCardMedia vehicle={vehicle} alt={line1} />
            <div className="vehicle-card__badges vehicle-card__badges--list">
              <span className="vehicle-card__badge">{year}</span>
              <span className="vehicle-card__badge">{km.toLocaleString('es-UY')} km</span>
            </div>
          </div>
          <div className="vehicle-card__body">
            <h3 className="vehicle-card__line1">{line1}</h3>
            {line2 ? <p className="vehicle-card__line2">{line2}</p> : null}
            <div className="vehicle-card__footer vehicle-card__footer--list">
              <p className="vehicle-card__price">
                <span className="vehicle-card__currency">{currency}</span> {amount}
              </p>
              <span className="vehicle-card__cta">
                {t('listings.card.viewDetail')}
                <span className="vehicle-card__cta-arrow" aria-hidden>
                  →
                </span>
              </span>
            </div>
          </div>
        </article>
      </Link>
    )
  }

  return (
    <Link to={`/vehiculos/${vehicle.id}`} className="vehicle-card-link">
      <article className="vehicle-card">
        <div className="vehicle-card__image-wrap">
          <VehicleCardMedia vehicle={vehicle} alt={line1} />
        </div>
        <div className="vehicle-card__body">
          <h3 className="vehicle-card__line1">{line1}</h3>
          {line2 ? <p className="vehicle-card__line2">{line2}</p> : null}
          <p className="vehicle-card__meta">
            {year} | {km.toLocaleString('es-UY')} km
          </p>
          <p className="vehicle-card__price">
            <span className="vehicle-card__currency">{currency}</span> {amount}
          </p>
        </div>
      </article>
    </Link>
  )
}

export default VehicleCard
